﻿import * as paths from '../utils/paths';
import { parseStyleRules, getStyleType, parse } from '../utils/styleParser';
import { parseStyleRules as parseWebglStyle } from '../utils/style/webglStyleParser';
import { parseStyleRules as parseCanvasStyle } from '../utils/style/canvasStyleParser';
import * as styleTypes from '../utils/style/styleTypes';

export default class StyleHandler {
    constructor(rootURL) {
        this.rootURL = rootURL;
        this.styles = {};
        this.systemStyles = {};
    }

    loadStyle(styleId, isSystem) {
        let stylePath = paths.STYLE_URL;

        styleId = styleId == null ? '' : '' + styleId;

        if (styleId == '') {
            styleId = 'sys_' + styleTypes.SYSTEM_STYLE_ALL;
        }

        if (styleId.indexOf('sys_') == 0) {
            isSystem = true;
        }

        if (isSystem) {
            stylePath = paths.SYSTEM_STYLE_URL;
        }

        return new Promise((resolve, reject) => {
            if (styleId == null) {
                resolve();
            }
            else if (this.styles[styleId] != null) {
                resolve();
            } else {
                $.ajax({
                    type: "GET",
                    url: this.rootURL + stylePath + styleId.replaceAll('sys_', ''),
                    async: true,
                    contentType: "application/json; charset=utf-8",
                    success: (result) => {
                        if (result != null) {
                            try {
                                if (isSystem) {
                                    let systemStyle = JSON.parse(result);
                                    this.styles[styleId] = systemStyle.Content;
                                    this.styles[systemStyle.Id] = systemStyle.Content;
                                    this.systemStyles[styleId] = systemStyle.Id;
                                } else {
                                    let styleDefinition = JSON.parse(result);
                                    this.styles[styleId] = styleDefinition;
                                }
                                resolve();
                            } catch (ex) {
                                resolve();
                            }
                        } else {
                            resolve();
                        }
                    },
                    error: (e) => {
                        resolve();
                    }
                });
            }
        });
    }

    applyStyle(styleId, style) {
        this.styles[styleId] = style;
    }

    getCanvasStyleById(styleId, type) {
        return parseStyleRules(this.styles[styleId], type, true);
    }

    getStyleById(styleId, type) {
        if (styleId == null) {
            return {
                styleType: styleTypes.STYLE_TYPE_WEBGL,
                style: {
                    featureStyle: [],
                    labelStyle: null
                }
            }
        }

        return parseStyleRules(this.styles[styleId], type, false);
    }

    getCombinedStyle(defaultStyleId, hoverStyleId, selectionStyleId) {
        if (this.styles[defaultStyleId] != null && this.styles[defaultStyleId].Type != null) {
            if (this.styles[defaultStyleId].Type.trim() == 'HEATMAP') {
                return {
                    styleType: styleTypes.STYLE_TYPE_HEATMAP,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'TEMATIC') {
                return {
                    styleType: styleTypes.STYLE_TYPE_THEMATIC,
                    style: this.styles[defaultStyleId]
                };
            } else if (this.styles[defaultStyleId].Type.trim() == 'CUSTOM') {
                return {
                    styleType: this.styles[defaultStyleId].Type,
                    styleSubType: this.styles[defaultStyleId].SubType,
                    style: this.styles[defaultStyleId]
                };
            }
        }

        let isDefaultWebgl = getStyleType(this.styles[defaultStyleId]) == styleTypes.STYLE_TYPE_WEBGL;
        let isHoverWebgl = getStyleType(this.styles[hoverStyleId]) == styleTypes.STYLE_TYPE_WEBGL;
        let isSelectionWebgl = getStyleType(this.styles[selectionStyleId]) == styleTypes.STYLE_TYPE_WEBGL;

        let forceCanvas = !(isDefaultWebgl && isHoverWebgl && isSelectionWebgl);

        let defaultStyle = parseStyleRules(this.styles[defaultStyleId], styleTypes.DEFAULT_STYLE_TYPE, forceCanvas);
        let hoverStyle = parseStyleRules(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE, forceCanvas);
        let selectionStyle = parseStyleRules(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE, forceCanvas);

        let result;

        if (forceCanvas) {
            result = JSON.parse(JSON.stringify(defaultStyle));

            if (defaultStyle.style.rules.length > 1) {
                defaultStyle.style.rules.forEach(rule => {
                    if (hoverStyle.style.rules.length > 0) {
                        let hoverRule = JSON.parse(JSON.stringify(hoverStyle.style.rules[0]));
                        hoverRule.filter = JSON.parse(JSON.stringify(rule.filter));
                        hoverRule.classificationIdx = rule.classificationIdx;

                        if (hoverRule.filter.predicates && hoverRule.filter.predicates.length) {
                            let statePredicate = hoverRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.HOVER_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(hoverRule);
                    }

                    if (selectionStyle.style.rules.length > 0) {
                        let selectRule = JSON.parse(JSON.stringify(selectionStyle.style.rules[0]));
                        selectRule.filter = JSON.parse(JSON.stringify(rule.filter));
                        selectRule.classificationIdx = rule.classificationIdx;

                        if (selectRule.filter.predicates && selectRule.filter.predicates.length) {
                            let statePredicate = selectRule.filter.predicates.find(predicate => predicate.propertyname == 'xstate');
                            if (statePredicate) {
                                statePredicate.literal = styleTypes.SELECT_STYLE_TYPE;
                            }
                        }

                        result.style.rules.push(selectRule);
                    }
                });
            } else {
                if (hoverStyle.style.rules.length > 0) {
                    hoverStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }

                if (selectionStyle.style.rules.length > 0) {
                    selectionStyle.style.rules.forEach(style => {
                        result.style.rules.push(style)
                    });
                }
            }
        } else {
            result = defaultStyle;

            if (hoverStyle.style.featureStyle.length > 0) {
                hoverStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }

            if (selectionStyle.style.featureStyle.length > 0) {
                selectionStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }
        }

        return result;
    }

    getThematicCombinedStyle(defaultStyleId, hoverStyleId, selectionStyleId, min, max) {
        let defaultStyleDescriptor = this._convertThematicToNormalStyle(defaultStyleId, min, max);

        let result;

        if (hoverStyleId == null) {
            let defaultStyle = parseCanvasStyle(defaultStyleDescriptor, styleTypes.DEFAULT_STYLE_TYPE);
            let hoverStyle = parseCanvasStyle(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE);
            let selectionStyle = parseCanvasStyle(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE);
            result = defaultStyle;

            if (hoverStyle.style.featureStyle != null && hoverStyle.style.featureStyle.length > 0) {
                hoverStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }

            if (selectionStyle.style.featureStyle != null && selectionStyle.style.featureStyle.length > 0) {
                selectionStyle.style.featureStyle.forEach(style => {
                    result.style.featureStyle.push(style);
                });
            }
        } else {
            let defaultStyle = parseCanvasStyle(defaultStyleDescriptor, styleTypes.DEFAULT_STYLE_TYPE);
            let hoverStyle = parseCanvasStyle(this.styles[hoverStyleId], styleTypes.HOVER_STYLE_TYPE);
            let selectionStyle = parseCanvasStyle(this.styles[selectionStyleId], styleTypes.SELECT_STYLE_TYPE);

            result = defaultStyle;

            if (hoverStyle.style.rules.length > 0) {
                hoverStyle.style.rules.forEach(style => {
                    result.style.rules.push(style)
                });
            }

            if (selectionStyle.style.rules.length > 0) {
                selectionStyle.style.rules.forEach(style => {
                    result.style.rules.push(style)
                });
            }
        }

        return result;
    }

    _convertThematicToNormalStyle(defaultStyleId, min, max) {
        if (min == null || max == null) {
            let thematic = this.styles[defaultStyleId];
            let rules = [];

            for (let i = 0; i < thematic.Intervals.length; i++) {
                let interval = thematic.Intervals[i];
                interval.min = this._customConvert(interval.min);
                interval.max = this._customConvert(interval.max);

                rules.push(this._getThematicRule(thematic.NameEntityField, i == 0 ? (interval.min - 1) : interval.min, i == thematic.Intervals.length - 1 ? (interval.max + 1) : interval.max, interval.color, thematic.Opacity));
            }

            return rules;
        }
        else {
            let thematic = this.styles[defaultStyleId];

            let delta = (max - min) / thematic.Intervals.length;
            let start = 0;

            let rules = [];

            for (let i = 0; i < thematic.Intervals.length; i++) {
                let interval = thematic.Intervals[i];
                let intervalMin = min + start;
                let intervalMax = intervalMin + delta;
                start += delta;

                interval.min = this._customConvert(intervalMin);
                interval.max = this._customConvert(intervalMax);

                rules.push(this._getThematicRule(thematic.NameEntityField, i == 0 ? (interval.min - 1) : interval.min, i == thematic.Intervals.length - 1 ? (interval.max + 1) : interval.max, interval.color, thematic.Opacity));
            }

            return rules;
        }
    }

    _customConvert(number) {
        if (number >= 1) {
            return Math.round(number);
        } else {
            return Math.floor(number);
        }
    }

    _getThematicRule(field, min, max, color, opacity) {
        let result = {
            "$type": "Sld.Rule, XPortal4.Core",
            "Name": "DefaultAllStyle",
            "Title": "",
            "MinScaleDenominatorSpecified": false,
            "MaxScaleDenominatorSpecified": false,
            "Items": [
                //{
                //    "$type": "Sld.RuleSymbolizerPointSymbolizer, XPortal4.Core",
                //    "Graphic": {
                //        "Mark": {
                //            "WellKnownName": "circle",
                //            "Fill": {
                //                "styling": {
                //                    "fill": color,
                //                    "fillOpacity": opacity
                //                }
                //            },
                //            "Stroke": {
                //                "styling": {
                //                    "stroke": color,
                //                    "strokeOpacity": 1,
                //                    "strokeWidth": 2
                //                }
                //            }
                //        },
                //        "Size": 10
                //    },
                //    "Id": 1,
                //    "NodeText": "Simbol punct",
                //    "index": 0,
                //    "_matchFilter": true
                //},
                //{
                //    "$type": "Sld.RuleSymbolizerLineSymbolizer, XPortal4.Core",
                //    "Stroke": {
                //        "styling": {
                //            "stroke": color,
                //            "strokeDasharray": "",
                //            "strokeLinecap": "butt",
                //            "strokeLinejoin": "miter",
                //            "strokeOpacity": 1,
                //            "strokeWidth": 2
                //        }
                //    },
                //    "Id": 2,
                //    "NodeText": "Simbol linie",
                //    "index": 1,
                //    "_matchFilter": true
                //},
                {
                    "$type": "Sld.RuleSymbolizerPolygonSymbolizer, XPortal4.Core",
                    "Fill": {
                        "styling": {
                            "fill": color,
                            "fillOpacity": opacity
                        }
                    },
                    "Stroke": {
                        "styling": {
                            "stroke": color,
                            "strokeDasharray": null,
                            "strokeLinecap": "butt",
                            "strokeLinejoin": "miter",
                            "strokeOpacity": 1,
                            "strokeWidth": 2
                        }
                    },
                    "Id": 3,
                    "NodeText": "Polygon Theme",
                    "index": 2,
                    "_matchFilter": true
                }
            ],
            "Id": 0,
            "NodeText": "DefaultAllStyle",
            "index": 0,
            "expanded": true,
            "selected": true,
            "ValidError": null,
            "Item": {
                "AND": [
                    {
                        ">=": [
                            field,
                            min
                        ]
                    },
                    {
                        "<": [
                            field,
                            max
                        ]
                    }
                ]
            },
            "MinScaleDenominator": null,
            "MaxScaleDenominator": null,
            "_matchFilter": true
        }

        return result;
    }
}